import Dialog from "@mui/material/Dialog";
import {
  Box,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const imageExtensions = [
  ".jpg",
  ".jpeg",
  ".png",
  ".bmp",
  ".tiff",
  ".tif",

  ".ico",
  ".heic",
  ".heif",
  ".raw",
  ".cr2",
  ".nef",
  ".orf",
  ".sr2",
];

const containsImageExtension = (filename, allowedImagesExtensions) => {
  return allowedImagesExtensions.some((ext) =>
    filename.toLowerCase().endsWith(ext)
  );
};

const PreviewAttachmentDialog = ({
  open,
  handleClose,
  attachmentDetails,
  providedImagesExtensions,
  fileData,
  title,
  closeLabel,
  showOpenInNewTab = false,
  openInNewTabLabel,
  fullScreenOnMobile = false,
}) => {
  // Labels follow the portal language unless the caller passes its own.
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  title = title ?? (isArabic ? "عرض المرفق" : "Attachment Preview");
  closeLabel = closeLabel ?? (isArabic ? "إغلاق" : "close");
  openInNewTabLabel = openInNewTabLabel ?? (isArabic ? "فتح في نافذة جديدة" : "Open in new tab");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = fullScreenOnMobile && isSmallScreen;

  // Support two modes:
  // 1. URL mode: { url, fileName } — displays directly without fetching (fast)
  // 2. File mode: { fileObject } — uses blob URL (for local file inputs)
  // Optional `kind` ("image" | "pdf" | other) overrides extension detection.
  // The legacy `fileData` prop and { name, path } shape are still accepted.
  const details = attachmentDetails || fileData || {};
  const { fileObject, kind } = details;
  const directUrl = details.url || details.path;
  const directFileName = details.fileName || details.name;

  const fileName = fileObject ? fileObject.name : directFileName || "";
  const normalizedKind = String(kind || "").toLowerCase();

  const isImageAttachment = normalizedKind
    ? normalizedKind === "image"
    : containsImageExtension(fileName, providedImagesExtensions || imageExtensions);
  const isPdfAttachment = normalizedKind
    ? normalizedKind === "pdf"
    : fileName.toLowerCase().endsWith(".pdf");

  // Get the display URL — use direct URL if available, otherwise create blob URL
  const previewUrl = directUrl || (fileObject ? URL.createObjectURL(fileObject) : "");

  const handleDownloadClick = () => {
    const link = document.createElement("a");
    link.href = previewUrl;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle
        sx={{ justifyContent: "space-between", display: "flex" }}
        id="customized-dialog-title"
      >
        <Typography
          component="div"
          sx={{ fontWeight: "bold", fontSize: "1.25rem" }}
        >
          {title}
        </Typography>
        <Stack direction="row" alignItems="center">
          {showOpenInNewTab && previewUrl && (
            <Tooltip title={openInNewTabLabel}>
              <IconButton
                component="a"
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={openInNewTabLabel}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton aria-label={closeLabel} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack justifyContent="center" alignItems="center">
          {isImageAttachment ? (
            <img
              src={previewUrl}
              style={{ maxWidth: "min(500px, 100%)", maxHeight: 500 }}
              alt={fileName || "preview"}
            />
          ) : isPdfAttachment ? (
            <embed
              src={previewUrl}
              title={fileName || title}
              type="application/pdf"
              width="100%"
              height={fullScreen ? "80vh" : "500px"}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Stack direction={"row"} gap={3}>
                <Typography>File Name:</Typography>
                <Typography component="div">{fileName}</Typography>
              </Stack>
              <IconButton onClick={handleDownloadClick}>
                <DownloadIcon color={"primary"} />
              </IconButton>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewAttachmentDialog;
